import React, { useState } from 'react'
import SEO from '../components/SEO'

export default function ForsikringPage() {
  const [currentStep, setCurrentStep] = useState(1)

  const [formData, setFormData] = useState({
    medlemskab_ansvar: true,
    oenskes_kasko: true,
    ejer_navn: '',
    dt_medlem_nr: '',
    ejer_adresse: '',
    post_nr: '',
    by: '',
    email: '',
    foedselsdato: '',
    telefonnummer: '',
    medlem_af_klub: 'Nej', // Ja / Nej
    klubnavn: '',
    liggeplads_sommer: '',
    liggeplads_vinter: '',
    uddannelse: 'Ingen søfartsudannelse',
    
    // Oplysninger om fartøjet
    baadtype: 'Motorbåd',
    baadfabrikant: '',
    bygge_nr: '',
    model: '',
    materiale: 'Glasfiber',
    laengde: '',
    bredde: '',
    dybgang: '',
    byggeaar: '',
    motor: 'Indenbords', // Indenbords, Udenbords, Ingen motor
    max_fart: '',
    motorfabrikant: '',
    motor_aargang: '',
    tyverialarm: false,
    fast_aksel: false,
    z_drev: false,
    sejl_drev: false,
    paahaengsmotor: false,
    z_drev_kasko: 'Nej', // Ja / Nej
    motor_nummer_1: '',
    hk_motor_1: '',
    motor_nummer_2: '',
    hk_motor_2: '',
    forsikringssum: '',
    antal_skadefri_aar: '0',
    antal_aars_sejlerfaring: '1',

    // Oplysning om evt. trailer der skal medforsikres
    trailer_fabrikant: '',
    trailer_aargang: '',
    trailer_indr_nr: '',
    trailer_stel_nr: '',
    trailer_vaerdi: '',
    bemaerkninger: '',

    // Honeypot
    website_url: ''
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const steps = [
    { id: 1, name: 'Ejer & Kontakt', icon: 'fa-user' },
    { id: 2, name: 'Fartøj', icon: 'fa-ship' },
    { id: 3, name: 'Motor & Udstyr', icon: 'fa-gears' },
    { id: 4, name: 'Trailer & Send', icon: 'fa-paper-plane' }
  ]

  const validateStep = (stepNumber) => {
    const errors = {}

    if (stepNumber === 1) {
      if (!formData.ejer_navn.trim()) errors.ejer_navn = 'Indtast venligst dit navn'
      if (!formData.email.trim()) {
        errors.email = 'Indtast venligst din e-mail'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.email = 'Indtast venligst en gyldig e-mailadresse'
      }
      if (!formData.telefonnummer.trim()) errors.telefonnummer = 'Indtast venligst dit telefonnummer'
      if (!formData.post_nr.trim()) errors.post_nr = 'Indtast postnr.'
      if (!formData.by.trim()) errors.by = 'Indtast by'
    } else if (stepNumber === 2) {
      if (!formData.baadfabrikant.trim() && !formData.model.trim()) {
        errors.baadfabrikant = 'Indtast bådfabrikant eller model'
      }
      if (!formData.forsikringssum.trim()) {
        errors.forsikringssum = 'Indtast ønsket forsikringssum'
      }
    } else if (stepNumber === 3) {
      if (formData.motor !== 'Ingen motor' && formData.motor !== '') {
        // Optional engine checks if applicable
      }
    }

    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]
      setErrorMessage(firstError)
      return false
    }

    setErrorMessage('')
    return true
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length))
      window.scrollTo({ top: 300, behavior: 'smooth' })
    }
  }

  const handlePrev = () => {
    setErrorMessage('')
    setFieldErrors({})
    setCurrentStep((prev) => Math.max(prev - 1, 1))
    window.scrollTo({ top: 300, behavior: 'smooth' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')

    // Validate all steps in order
    if (!validateStep(1)) {
      setCurrentStep(1)
      window.scrollTo({ top: 300, behavior: 'smooth' })
      return
    }
    if (!validateStep(2)) {
      setCurrentStep(2)
      window.scrollTo({ top: 300, behavior: 'smooth' })
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_type: 'Forsikringstilbud - Danske Tursejlere',
          ...formData,
        }),
      })

      if (response.ok) {
        setSubmitted(true)
        window.scrollTo({ top: 200, behavior: 'smooth' })
      } else {
        const errorData = await response.json().catch(() => ({}))
        setErrorMessage(errorData?.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring på 40 20 70 34.')
      }
    } catch (err) {
      setErrorMessage('Der opstod en netværksfejl. Prøv venligst igen eller kontakt os på tlf. 40 20 70 34.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <SEO
        title="Bådforsikring - Nordfyn Marine"
        description="Få uforpligtende tilbud på bådforsikring via Danske Tursejlere og Nordfyn Marine."
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header Hero */}
        <div className="bg-[#0B1B2B] text-white p-8 sm:p-12 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#2BABE3]">
              Danske Tursejlere & Nordfyn Marine
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Bådforsikring Tilbud
            </h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Vi samarbejder med Danske Tursejlere, som tilbyder attraktive og uforpligtende forsikringstilbud til bådejere. Udfyld formularen trin for trin og modtag et skræddersyet tilbud på e-mail inden for 1-2 hverdage.
            </p>
          </div>
        </div>

        {/* Content & Form Container */}
        <div className="bg-white p-6 sm:p-10 rounded-2xl border border-slate-200 shadow-sm space-y-8 text-slate-700 text-sm leading-relaxed">
          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 sm:p-12 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                <i className="fa-solid fa-check" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-emerald-900">
                Tak for din forespørgsel!
              </h2>
              <p className="text-sm text-emerald-800 max-w-md mx-auto">
                Vi har modtaget dine oplysninger til forsikringstilbuddet. Du vil modtage et uforpligtende tilbud på din e-mail inden for et par hverdage.
              </p>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false)
                    setCurrentStep(1)
                  }}
                  className="inline-flex items-center gap-2 bg-[#0B1B2B] hover:bg-[#122B42] text-white px-6 py-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                >
                  <i className="fa-solid fa-rotate-left" />
                  <span>Send endnu en forespørgsel</span>
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Step Progress Navigation */}
              <div className="mb-8 border-b border-slate-200 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1E7BC1]">
                    Trin {currentStep} af {steps.length}
                  </span>
                  <span className="text-xs font-semibold text-slate-500">
                    {steps[currentStep - 1].name}
                  </span>
                </div>

                {/* Progress bar line */}
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mb-6">
                  <div
                    className="bg-[#1E7BC1] h-full transition-all duration-300 ease-in-out"
                    style={{ width: `${(currentStep / steps.length) * 100}%` }}
                  />
                </div>

                {/* Step Tabs / Buttons */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {steps.map((step) => {
                    const isActive = currentStep === step.id
                    const isCompleted = currentStep > step.id
                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => {
                          if (step.id < currentStep) {
                            setCurrentStep(step.id)
                          } else if (step.id > currentStep && validateStep(currentStep)) {
                            setCurrentStep(step.id)
                          }
                        }}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border text-left text-xs font-bold transition-all cursor-pointer ${
                          isActive
                            ? 'bg-[#0B1B2B] text-white border-[#0B1B2B] shadow-sm'
                            : isCompleted
                            ? 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200'
                            : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            isActive
                              ? 'bg-[#1E7BC1] text-white'
                              : isCompleted
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {isCompleted ? <i className="fa-solid fa-check" /> : step.id}
                        </div>
                        <span className="truncate">{step.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {errorMessage && (
                <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium flex items-center gap-3 animate-fadeIn">
                  <i className="fa-solid fa-triangle-exclamation text-rose-500 text-base flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-8">
                {/* Antispam Honeypot */}
                <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    value={formData.website_url}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>

                {/* STEP 1: Ejer & Kontakt */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    {/* Checkboxes: Medlemskab / Kasko */}
                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Forsikringstype
                      </h3>
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          name="medlemskab_ansvar"
                          checked={formData.medlemskab_ansvar}
                          onChange={handleChange}
                          className="w-4 h-4 rounded text-[#1E7BC1] focus:ring-[#1E7BC1] border-slate-300"
                        />
                        <span className="font-semibold text-slate-900">
                          Medlemskab Danske Tursejlere (inkl. ansvarsforsikring)
                        </span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          name="oenskes_kasko"
                          checked={formData.oenskes_kasko}
                          onChange={handleChange}
                          className="w-4 h-4 rounded text-[#1E7BC1] focus:ring-[#1E7BC1] border-slate-300"
                        />
                        <span className="font-semibold text-slate-900">
                          Ønskes tilbud på kaskoforsikring
                        </span>
                      </label>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-lg font-serif font-bold text-slate-900 border-b border-slate-200 pb-2">
                        Ejers oplysninger
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Fulde navn <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="ejer_navn"
                            value={formData.ejer_navn}
                            onChange={handleChange}
                            placeholder="Fx Anders Jensen"
                            className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                              fieldErrors.ejer_navn ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            DT Medlemsnummer (hvis du allerede er medlem)
                          </label>
                          <input
                            type="text"
                            name="dt_medlem_nr"
                            value={formData.dt_medlem_nr}
                            onChange={handleChange}
                            placeholder="Valgfrit"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Fødselsdato
                          </label>
                          <input
                            type="text"
                            name="foedselsdato"
                            placeholder="DD/MM/ÅÅÅÅ"
                            value={formData.foedselsdato}
                            onChange={handleChange}
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Adresse
                          </label>
                          <input
                            type="text"
                            name="ejer_adresse"
                            value={formData.ejer_adresse}
                            onChange={handleChange}
                            placeholder="Gade og husnummer"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Postnr. <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="post_nr"
                            value={formData.post_nr}
                            onChange={handleChange}
                            placeholder="Fx 5450"
                            className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                              fieldErrors.post_nr ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            By <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="by"
                            value={formData.by}
                            onChange={handleChange}
                            placeholder="Fx Otterup"
                            className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                              fieldErrors.by ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            E-mail <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="din@email.dk"
                            className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                              fieldErrors.email ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Telefonnummer <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="telefonnummer"
                            value={formData.telefonnummer}
                            onChange={handleChange}
                            placeholder="Fx 40207034"
                            className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                              fieldErrors.telefonnummer ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Er du medlem af en sejlklub?
                          </label>
                          <div className="flex items-center gap-6 py-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="medlem_af_klub"
                                value="Ja"
                                checked={formData.medlem_af_klub === 'Ja'}
                                onChange={handleChange}
                                className="text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Ja</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="medlem_af_klub"
                                value="Nej"
                                checked={formData.medlem_af_klub === 'Nej'}
                                onChange={handleChange}
                                className="text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Nej</span>
                            </label>
                          </div>
                        </div>

                        {formData.medlem_af_klub === 'Ja' && (
                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Klubnavn
                            </label>
                            <input
                              type="text"
                              name="klubnavn"
                              value={formData.klubnavn}
                              onChange={handleChange}
                              placeholder="Fx Bogense Sejlklub"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Liggeplads sommer
                          </label>
                          <input
                            type="text"
                            name="liggeplads_sommer"
                            value={formData.liggeplads_sommer}
                            onChange={handleChange}
                            placeholder="Fx Kerteminde Marina"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Liggeplads vinter
                          </label>
                          <input
                            type="text"
                            name="liggeplads_vinter"
                            value={formData.liggeplads_vinter}
                            onChange={handleChange}
                            placeholder="Fx På land / Hal / I vandet"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Søfartsuddannelse / beviser
                          </label>
                          <select
                            name="uddannelse"
                            value={formData.uddannelse}
                            onChange={handleChange}
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          >
                            <option value="Ingen søfartsudannelse">Ingen formel søfartsuddannelse</option>
                            <option value="Speedbådskort">Speedbådskort</option>
                            <option value="Duelighedsbevis">Duelighedsbevis</option>
                            <option value="Yacht Skipper 3">Yacht Skipper 3</option>
                            <option value="Anden søfartsudannelse">Anden søfartsuddannelse</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2: Fartøjsoplysninger */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-serif font-bold text-slate-900 border-b border-slate-200 pb-2">
                      Oplysninger om fartøjet
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Bådtype
                        </label>
                        <select
                          name="baadtype"
                          value={formData.baadtype}
                          onChange={handleChange}
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        >
                          <option value="Motorbåd">Motorbåd</option>
                          <option value="Sejlbåd">Sejlbåd</option>
                          <option value="Motorsejler">Motorsejler</option>
                          <option value="Sejl/Motor jolle">Sejl/Motor jolle</option>
                          <option value="Rib båd">Rib båd</option>
                          <option value="Anden type">Anden bådtype</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Materiale
                        </label>
                        <select
                          name="materiale"
                          value={formData.materiale}
                          onChange={handleChange}
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        >
                          <option value="Glasfiber">Glasfiber</option>
                          <option value="Træ">Træ</option>
                          <option value="Aluminium">Aluminium</option>
                          <option value="Stål">Stål</option>
                          <option value="Andet">Andet</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Bådfabrikant / Mærke <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="baadfabrikant"
                          value={formData.baadfabrikant}
                          onChange={handleChange}
                          placeholder="Fx Nimbus, Bavaria, Maxi, Jupiter"
                          className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                            fieldErrors.baadfabrikant ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Model
                        </label>
                        <input
                          type="text"
                          name="model"
                          value={formData.model}
                          onChange={handleChange}
                          placeholder="Fx 33, 300, 38 Cruiser"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Byggeår / Årgang
                        </label>
                        <input
                          type="text"
                          name="byggeaar"
                          value={formData.byggeaar}
                          onChange={handleChange}
                          placeholder="Fx 1998"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Byggenummer / Skrog-nr. (HIN / CIN)
                        </label>
                        <input
                          type="text"
                          name="bygge_nr"
                          value={formData.bygge_nr}
                          onChange={handleChange}
                          placeholder="Valgfrit"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Længde (m / fod)
                        </label>
                        <input
                          type="text"
                          name="laengde"
                          value={formData.laengde}
                          onChange={handleChange}
                          placeholder="Fx 10,20 m eller 33 fod"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Bredde (m)
                        </label>
                        <input
                          type="text"
                          name="bredde"
                          value={formData.bredde}
                          onChange={handleChange}
                          placeholder="Fx 3,35 m"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Dybgang (m)
                        </label>
                        <input
                          type="text"
                          name="dybgang"
                          value={formData.dybgang}
                          onChange={handleChange}
                          placeholder="Fx 0,90 m"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Ønsket forsikringssum kr. <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="forsikringssum"
                          value={formData.forsikringssum}
                          onChange={handleChange}
                          placeholder="Fx 350.000"
                          className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all ${
                            fieldErrors.forsikringssum ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Antal skadefri år
                        </label>
                        <input
                          type="text"
                          name="antal_skadefri_aar"
                          value={formData.antal_skadefri_aar}
                          onChange={handleChange}
                          placeholder="Fx 5"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Antal års sejlerfaring
                        </label>
                        <input
                          type="text"
                          name="antal_aars_sejlerfaring"
                          value={formData.antal_aars_sejlerfaring}
                          onChange={handleChange}
                          placeholder="Fx 10"
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Motor & Udstyr */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-serif font-bold text-slate-900 border-b border-slate-200 pb-2">
                      Motor & Udstyr
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                          Motortype
                        </label>
                        <select
                          name="motor"
                          value={formData.motor}
                          onChange={handleChange}
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                        >
                          <option value="Indenbords">Indenbordsmotor</option>
                          <option value="Udenbords">Udenbordsmotor (påhængsmotor)</option>
                          <option value="Ingen motor">Ingen motor</option>
                        </select>
                      </div>

                      {formData.motor !== 'Ingen motor' && (
                        <>
                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Motorfabrikat / Mærke
                            </label>
                            <input
                              type="text"
                              name="motorfabrikant"
                              value={formData.motorfabrikant}
                              onChange={handleChange}
                              placeholder="Fx Volvo Penta, Yanmar, Mercury"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Motor Årgang
                            </label>
                            <input
                              type="text"
                              name="motor_aargang"
                              value={formData.motor_aargang}
                              onChange={handleChange}
                              placeholder="Fx 2005"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              HK (Hestekræfter)
                            </label>
                            <input
                              type="text"
                              name="hk_motor_1"
                              value={formData.hk_motor_1}
                              onChange={handleChange}
                              placeholder="Fx 200"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Max fart (knob)
                            </label>
                            <input
                              type="text"
                              name="max_fart"
                              value={formData.max_fart}
                              onChange={handleChange}
                              placeholder="Fx 22 knob"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Motornummer
                            </label>
                            <input
                              type="text"
                              name="motor_nummer_1"
                              value={formData.motor_nummer_1}
                              onChange={handleChange}
                              placeholder="Valgfrit"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Evt. Motor 2 (HK / type)
                            </label>
                            <input
                              type="text"
                              name="hk_motor_2"
                              value={formData.hk_motor_2}
                              onChange={handleChange}
                              placeholder="Valgfrit (ved dobbeltinstallation)"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                            />
                          </div>

                          {/* Udstyr & Drev Checkboxes */}
                          <div className="md:col-span-2 bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                              <input
                                type="checkbox"
                                name="tyverialarm"
                                checked={formData.tyverialarm}
                                onChange={handleChange}
                                className="rounded text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Tyverialarm</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                              <input
                                type="checkbox"
                                name="fast_aksel"
                                checked={formData.fast_aksel}
                                onChange={handleChange}
                                className="rounded text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Fast aksel</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                              <input
                                type="checkbox"
                                name="z_drev"
                                checked={formData.z_drev}
                                onChange={handleChange}
                                className="rounded text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Z-drev</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                              <input
                                type="checkbox"
                                name="sejl_drev"
                                checked={formData.sejl_drev}
                                onChange={handleChange}
                                className="rounded text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Sejl-drev</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                              <input
                                type="checkbox"
                                name="paahaengsmotor"
                                checked={formData.paahaengsmotor}
                                onChange={handleChange}
                                className="rounded text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Påhængsmotor</span>
                            </label>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                              Skal Z-drev medforsikres ved tegning af kasko?
                            </label>
                            <div className="flex items-center gap-6 py-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                type="radio"
                                name="z_drev_kasko"
                                value="Ja"
                                checked={formData.z_drev_kasko === 'Ja'}
                                onChange={handleChange}
                                className="text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Ja</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="z_drev_kasko"
                                value="Nej"
                                checked={formData.z_drev_kasko === 'Nej'}
                                onChange={handleChange}
                                className="text-[#1E7BC1] focus:ring-[#1E7BC1]"
                              />
                              <span>Nej</span>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

                {/* STEP 4: Trailer, Bemærkninger & Send */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h2 className="text-xl font-serif font-bold text-slate-900 border-b border-slate-200 pb-2">
                        Oplysning om evt. trailer der skal medforsikres
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Trailer fabrikat
                          </label>
                          <input
                            type="text"
                            name="trailer_fabrikant"
                            value={formData.trailer_fabrikant}
                            onChange={handleChange}
                            placeholder="Fx Brenderup, Variant"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Årgang
                          </label>
                          <input
                            type="text"
                            name="trailer_aargang"
                            value={formData.trailer_aargang}
                            onChange={handleChange}
                            placeholder="Fx 2018"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Indreg. nr. (nummerplade)
                          </label>
                          <input
                            type="text"
                            name="trailer_indr_nr"
                            value={formData.trailer_indr_nr}
                            onChange={handleChange}
                            placeholder="Fx AB 12 34"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Stel nr.
                          </label>
                          <input
                            type="text"
                            name="trailer_stel_nr"
                            value={formData.trailer_stel_nr}
                            onChange={handleChange}
                            placeholder="Stelnummer"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                            Trailer værdi kr.
                          </label>
                          <input
                            type="text"
                            name="trailer_vaerdi"
                            value={formData.trailer_vaerdi}
                            onChange={handleChange}
                            placeholder="Fx 25.000"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 pt-2">
                      <h2 className="text-xl font-serif font-bold text-slate-900 border-b border-slate-200 pb-2">
                        Bemærkninger & særlige ønsker
                      </h2>

                      <div>
                        <textarea
                          name="bemaerkninger"
                          rows={4}
                          value={formData.bemaerkninger}
                          onChange={handleChange}
                          placeholder="Skriv evt. bemærkninger, særligt udstyr eller supplerende oplysninger her..."
                          className="w-full bg-slate-50 border border-slate-300 rounded-lg p-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#1E7BC1] focus:border-transparent outline-none transition-all resize-y"
                        />
                      </div>
                    </div>

                    {/* Summary box before submit */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3 text-xs text-slate-600">
                      <div className="font-bold text-slate-900 uppercase tracking-wider">
                        Opsamling på din forespørgsel:
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><strong className="text-slate-800">Ejer:</strong> {formData.ejer_navn || '-'}</div>
                        <div><strong className="text-slate-800">E-mail:</strong> {formData.email || '-'}</div>
                        <div><strong className="text-slate-800">Tlf:</strong> {formData.telefonnummer || '-'}</div>
                        <div><strong className="text-slate-800">By:</strong> {formData.post_nr} {formData.by}</div>
                        <div><strong className="text-slate-800">Fartøj:</strong> {formData.baadfabrikant} {formData.model} ({formData.baadtype})</div>
                        <div><strong className="text-slate-800">Forsikringssum:</strong> {formData.forsikringssum ? `${formData.forsikringssum} kr.` : '-'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step Controls / Navigation */}
                <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-200">
                  {currentStep > 1 ? (
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="w-full sm:w-auto px-6 py-3 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl font-bold text-xs transition-colors flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <i className="fa-solid fa-arrow-left" />
                      <span>Forrige trin</span>
                    </button>
                  ) : <div />}

                  {currentStep < steps.length ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full sm:w-auto px-8 py-3 bg-[#1E7BC1] hover:bg-[#16649f] text-white rounded-xl font-bold text-xs transition-colors shadow-sm flex items-center justify-center gap-2 ml-auto cursor-pointer"
                    >
                      <span>Næste trin</span>
                      <i className="fa-solid fa-arrow-right" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:w-auto px-10 py-3.5 bg-[#0B1B2B] hover:bg-[#122B42] text-white rounded-xl font-bold text-sm transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-50 ml-auto cursor-pointer hover:shadow-lg active:scale-98"
                    >
                      {submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          <span>Sender forespørgsel...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane" />
                          <span>Send tilbudsforspørgsel</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
